import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import client from "@culturehq/client";
import { Pagination, Panel } from "@culturehq/components";

import { useDoor } from "app/hooks";
import { parseQueryString, Trans } from "app/utils";

import Photo from "./gallery/Photo";
import EmptyGallery from "./gallery/EmptyGallery";
import LightboxModal from "./gallery/LightboxModal";

const Gallery = () => {
  const history = useHistory();
  const { location } = history;

  const [fetching, setFetching] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [pagination, setPagination] = useState({});
  const [activePhoto, setActivePhoto] = useState(null);

  const { page } = parseQueryString(location.search);
  const onPageClick = nextPage => history.push(`/photos?page=${nextPage}`);

  const [index, setIndex] = useState(() => (
    location.hash ? parseInt(location.hash.slice(1), 10) : null
  ));

  const { open, onOpen, onClose } = useDoor(index !== null);

  useEffect(
    () => {
      setFetching(true);

      client.getPhotoGallery({ page }).then(response => {
        setPhotos(response.photos);
        setPagination(response.pagination);
        setFetching(false);

        if (index !== null && (index < response.photos.length)) {
          setActivePhoto(response.photos[index === -1 ? response.photos.length - 1 : index]);
        }
      });
    },
    [page, setFetching, index]
  );

  const onLightboxClose = () => {
    onClose();
    setIndex(null);
  };

  const onPhotoChange = nextIndex => {
    const { currentPage, totalPages } = pagination;

    if (nextIndex === -1) {
      setIndex(-1);
      onPageClick(currentPage === 1 ? totalPages : currentPage - 1);
    } else if (nextIndex === photos.length) {
      setIndex(0);
      onPageClick(currentPage === totalPages ? 1 : currentPage + 1);
    } else {
      setIndex(nextIndex);
      onOpen();
    }
  };

  const onPhotoDelete = photo => {
    setPhotos(current => current.filter(currentPhoto => currentPhoto.id !== photo.id));
    onClose();
  };

  const onPhotoCheerToggle = (photoId, cheerForUser, cheersCount) => {
    setPhotos(current => current.map(currentPhoto => (
      currentPhoto.id === photoId ? ({ ...currentPhoto, cheerForUser, cheersCount }) : currentPhoto
    )));
  };

  const onPhotoUpdate = photo => {
    setPhotos(current => current.filter(currentPhoto => (
      currentPhoto.id === photo.id ? photo : currentPhoto
    )));
    if (activePhoto.id === photo.id) {
      setActivePhoto({ ...activePhoto, ...photo });
    }
  };

  return (
    <>
      <Panel className="gallery" limitWidth>
        <Panel.Heading primary>
          <Trans>Photos</Trans>
        </Panel.Heading>
        <Panel.LoaderBody loading={fetching}>
          <div className="clearfix">
            {photos.map(photo => (
              <Photo
                key={photo.id}
                photos={photos}
                photo={photo}
                onPhotoChange={onPhotoChange}
              />
            ))}
          </div>
          {photos.length === 0 && <EmptyGallery />}
          <Pagination {...pagination} onClick={onPageClick} />
        </Panel.LoaderBody>
      </Panel>
      <LightboxModal
        activePhoto={activePhoto}
        photos={photos}
        modalIsOpen={open}
        onClose={onLightboxClose}
        onPhotoChange={onPhotoChange}
        onDelete={onPhotoDelete}
        onPhotoCheerToggle={onPhotoCheerToggle}
        onPhotoUpdate={onPhotoUpdate}
      />
    </>
  );
};

export default Gallery;
